import { useMemo } from "react";
import { useLang } from "../context/LangContext";
import config, { getEnabledSocialMedia, getContactInfo } from "../config";

/**
 * Custom hook to access application configuration
 * @returns {Object} Configuration object with helper methods
 */
export const useConfig = () => {
  const { lang } = useLang();

  const configData = useMemo(() => {
    return {
      // Basic config
      ...config,

      // Helper methods
      getEnabledSocialMedia: () => getEnabledSocialMedia(),
      getContactInfo: () => getContactInfo(lang),

      // Language-specific helpers
      getCompanyName: () =>
        lang === "ar" ? config.company.name : config.company.nameEn,
      getCompanyDescription: () =>
        lang === "ar"
          ? config.company.description
          : config.company.descriptionEn,

      // Social media helpers
      getSocialMediaLinks: () => getEnabledSocialMedia(),

      // Contact helpers
      getContactEmail: () => config.contact.email,
      getContactPhone: () => config.contact.phone,
      getContactMobile: () => config.contact.mobile,
      getContactWhatsApp: () => config.contact.whatsapp,
      getContactAddress: () => getContactInfo(lang).address,
      getWorkingHours: () => getContactInfo(lang).workingHours,

      // Feature flags
      isFeatureEnabled: (feature) => config.app.features[feature] || false,

      // API helpers
      getApiUrl: (endpoint = "") => `${config.api.baseURL}${endpoint}`,

      // Payment helpers
      getPaymentMethods: () => config.payment.methods,
      isPaymentMethodEnabled: (method) =>
        config.payment.methods.includes(method),

      // Shipping helpers
      getShippingCost: (amount) => {
        if (amount >= config.shipping.freeShippingThreshold) {
          return 0;
        }
        return config.shipping.standardShippingCost;
      },
      getExpressShippingCost: () => config.shipping.expressShippingCost,
      getEstimatedDelivery: (isExpress = false) => {
        return isExpress
          ? config.shipping.estimatedDeliveryDays.express
          : config.shipping.estimatedDeliveryDays.standard;
      },
    };
  }, [lang]);

  return configData;
};

export default useConfig;



